"use client"

import { useState, useEffect, useCallback } from "react"
import { useMediaQuery, useTheme } from "@mui/material"
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material"
import {
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  CloudUpload as CloudUploadIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import { motion } from "framer-motion"
import axios from "axios"
import apiClient from "../utils/apiClient"

const API_BASE_URL = process.env.REACT_APP_API_URL
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
const IMPORT_API_URL = `${API_BASE_URL}/users/import`
const PRESIGN_API_URL = `${API_BASE_URL}/getUrl`
const EXPORT_API_URL = `${API_BASE_URL}/users/export`

const pageSize = 60

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const SEARCH_API_URL = `${API_BASE_URL}/list`

const UserOrdersPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [currentPage, setCurrentPage] = useState(1)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [searchQuery, setSearchQuery] = useState("")
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [activeCount, setActiveCount] = useState(0)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0,
    hasMore: false,
  })

  const handleOpenImportDialog = () => setImportDialogOpen(true)
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false)
    setSelectedFile(null)
    setUploadProgress(0)
  }
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file && !file.name.endsWith(".xlsx")) {
      toast.error("Only .xlsx files are allowed")
      setSelectedFile(null)
      return
    }
    setSelectedFile(file)
  }

  // Upload to S3 using presigned URL from API
  const handleImportUsers = async () => {
    if (!selectedFile) {
      toast.error("Please select a file")
      return
    }
    setImporting(true)
    setUploadProgress(0)
    try {
      const key = `import/${selectedFile.name}`
      const token = localStorage.getItem("idToken")
      // 1. Get presigned URL from your backend
      const presignRes = await apiClient.get(`/getUrl`, {
        params: { key: encodeURIComponent(key) },
      })
      const { url } = presignRes.data
      // 2. Upload file to S3 using the presigned URL
      await axios.put(url, selectedFile, {
        headers: { "Content-Type": XLSX_MIME },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(percent)
        },
      })
      setUploadProgress(100)

      // 3. Notify backend to process the file (if needed)
      const apiUrl = `${IMPORT_API_URL}?key=${encodeURIComponent(key)}`
      const resp = await apiClient.post(`/users/import?key=${encodeURIComponent(key)}`, {})

      if (resp.data && resp.data.message) {
        toast.success(resp.data.message)
      } else {
        toast.success("Users imported successfully!")
      }
      handleCloseImportDialog()
      // Refresh the user list after import
      handleSearch(searchQuery, 1)
    } catch (err) {
      toast.error("Failed to import users: " + (err?.response?.data?.message || err.message))
    } finally {
      setImporting(false)
      setUploadProgress(0)
    }
  }

  const handleExportUsers = async () => {
    setExportLoading(true)
    try {
      const token = localStorage.getItem("idToken")

      // Build params object similar to search functionality
      const params = {}

      // Only add search parameter if query is not empty
      if (searchQuery.trim()) {
        params.search = searchQuery
      }

      const response = await apiClient.get(`/users/export`, {
        params,
      })

      const { downloadUrl, filename } = response.data

      if (!downloadUrl) {
        throw new Error("Download URL missing from response")
      }

      const link = document.createElement("a")
      link.href = downloadUrl
      link.setAttribute("download", filename || "users-export.xlsx")
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success("Users exported successfully!")
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        toast.error("Session expired. Redirecting to login...")
        setTimeout(() => {
          localStorage.removeItem("idToken")
          window.location.href = "/login"
        }, 2000)
        return
      }
      toast.error("Failed to export users: " + (error?.response?.data?.message || error.message))
    } finally {
      setExportLoading(false)
    }
  }

  const handleSearch = useCallback(
    async (query, page = currentPage) => {
      setLoading(true)
      try {
        const token = localStorage.getItem("idToken")
        const params = {
          page: page,
          limit: pageSize, // Changed from 20 to pageSize (60)
        }

        // Only add search parameter if query is not empty
        if (query.trim()) {
          params.search = query
        }

        const response = await apiClient.get(`/list`, {
          params,
        })

        if (response.data && response.data.data) {
          setUsers(response.data.data)
          setTotalCount(response.data.totalCount || response.data.pagination?.total || 0)
          setActiveCount(response.data.activeCount || 0)
          // Store pagination info from API response
          if (response.data.pagination) {
            setPagination(response.data.pagination)
          }
        } else {
          setUsers([])
          setTotalCount(0)
          setActiveCount(0)
          setPagination({
            page: 1,
            limit: pageSize,
            total: 0,
            totalPages: 0,
            hasMore: false,
          })
        }
        setCurrentPage(page)
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          toast.error("Session expired. Redirecting to login...")
          setTimeout(() => {
            localStorage.removeItem("idToken")
            window.location.href = "/login"
          }, 2000)
          return
        }
        toast.error("Error fetching users: " + (error?.response?.data?.message || error.message))
        setUsers([])
        setTotalCount(0)
        setActiveCount(0)
        setPagination({
          page: 1,
          limit: pageSize,
          total: 0,
          totalPages: 0,
          hasMore: false,
        })
      } finally {
        setLoading(false)
      }
    },
    [currentPage],
  )

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    if (value === "") {
      handleSearch("", 1)
    }
  }



  // Fetch users on initial load
  useEffect(() => {
    handleSearch("", 1)
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery, 1) // Always start from page 1 when searching
    }, 500) // 500ms delay after typing stops

    return () => clearTimeout(timer)
  }, [searchQuery])

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1
      handleSearch(searchQuery, newPage)
    }
  }

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      const newPage = currentPage + 1
      handleSearch(searchQuery, newPage)
    }
  }

  const handlePageClick = (pageNum) => {
    if (pageNum !== currentPage) {
      handleSearch(searchQuery, pageNum)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: { xs: 1, sm: 2, md: 3 }, pb: { xs: 20, sm: 1 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              mb: 1,
              p: 1,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: 3,
              color: "white",
              position: "sticky",
              top: 0,
              zIndex: 1000,
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 30% 20%, rgba(44, 62, 80, 0.2) 0%, transparent 50%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: "50%",
                      p: 1,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
                      User Management
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Manage users
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      py: 0.5,
                      boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #16B1C8 0%, #20d5d5 100%)",
                        boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                      },
                    }}
                    onClick={handleOpenImportDialog}
                  >
                    {isMobile ? "Import" : "Import Users"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      py: 0.5,
                      boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #16B1C8 0%, #20d5d5 100%)",
                        boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                      },
                    }}
                    component="a"
                    href="/sample.xlsx"
                    download
                  >
                    {isMobile ? "Sample" : "Download Sample"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      py: 0.5,
                      boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #16B1C8 0%, #20d5d5 100%)",
                        boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                        color: "rgba(0, 0, 0, 0.26)",
                      },
                    }}
                    onClick={handleExportUsers}
                    disabled={exportLoading}
                  >
                    {exportLoading ? "Exporting..." : isMobile ? "Export" : "Export Users"}
                  </Button>
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: 120
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TrendingUpIcon />
                      {totalCount}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Users
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: 120
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1, color: "#4ade80" }}>
                      <PersonIcon />
                      {activeCount}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Active Users
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Search Bar */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Email"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#16B1C8", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: loading && (
                  <InputAdornment position="end">
                    <CircularProgress size={16} sx={{ color: "#16B1C8" }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#16B1C8",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#16B1C8",
                  },
                },
              }}
            />
          </Box>
        </motion.div>

        {/* Table */}
        <motion.div variants={itemVariants}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              height: "500px", // Increased height to accommodate more records (60 instead of 10)
              overflowY: "auto", // Enable vertical scrolling
              overflowX: "hidden", // Prevent horizontal scrolling
              WebkitOverflowScrolling: "touch",
              '&::-webkit-scrollbar': {
                width: { xs: 4, sm: 6 },
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(32, 213, 213, 0.5)',
                borderRadius: 3,
                '&:hover': {
                  background: 'rgba(32, 213, 213, 0.7)',
                },
              },
            }}
          >
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 50%, #0891b2 100%)",
                    position: "relative",

                    "& .MuiTableCell-root": {
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                    },
                  }}
                >
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Job Title</TableCell>
                  <TableCell>Manager Email</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ height: '400px', p: 0 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                      }}>
                        <CircularProgress size={40} sx={{ color: "#16B1C8" }} />
                        <Typography variant="body1" sx={{ mt: 2, color: "#64748b" }}>
                          Loading
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : users.length > 0 ? (
                  users.map((user, idx) => (
                    <TableRow key={user.id || idx}>
                      <TableCell sx={{ py: 2 }}>{user.employee_id}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.email}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.department}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.display_name}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.job_title}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.manager_email}</TableCell>
                      <TableCell sx={{ py: 2 }}>{user.status}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, borderBottom: 'none', height: '400px', p: 0 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                      }}>
                        <Typography variant="body1" sx={{ color: "#64748b" }}>
                          No users found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </motion.div>

        {/* Pagination Controls */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
              mb: { xs: 15, sm: 0 },
              gap: 2,
              p: 1.5,
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid #e2e8f0",
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <IconButton
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  backgroundColor: currentPage > 1 ? "#20d5d5" : "#e2e8f0",
                  color: currentPage > 1 ? "white" : "#94a3b8",
                  "&:hover": {
                    backgroundColor: currentPage > 1 ? "#16B1C8" : "#e2e8f0",
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </motion.div>
            {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
              // Show first 5 pages, last 5 pages, or pages around current page
              let pageNum
              if (pagination.totalPages <= 10) {
                pageNum = i + 1
              } else if (currentPage <= 5) {
                pageNum = i + 1
              } else if (currentPage >= pagination.totalPages - 4) {
                pageNum = pagination.totalPages - 9 + i
              } else {
                pageNum = currentPage - 4 + i
              }

              return (
                <motion.div key={pageNum} whileHover={{ scale: 0.9 }} whileTap={{ scale: 0.9 }}>
                  <Button
                    onClick={() => handlePageClick(pageNum)}
                    variant={pageNum === currentPage ? "contained" : "outlined"}
                    sx={{
                      minWidth: 36,
                      height: 34,
                      borderRadius: 2,
                      fontWeight: 600,
                      ...(pageNum === currentPage
                        ? {
                            background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                            boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                            color: "white",
                            border: "none",
                          }
                        : {
                            borderColor: "#d1d5db",
                            color: "#374151",
                            "&:hover": {
                              borderColor: "#20d5d5",
                              backgroundColor: "rgba(32, 213, 213, 0.05)",
                            },
                          }),
                    }}
                  >
                    {pageNum}
                  </Button>
                </motion.div>
              )
            })}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === pagination.totalPages}
                sx={{
                  backgroundColor: currentPage < pagination.totalPages ? "#20d5d5" : "#e2e8f0",
                  color: currentPage < pagination.totalPages ? "white" : "#94a3b8",
                  "&:hover": {
                    backgroundColor: currentPage < pagination.totalPages ? "#16B1C8" : "#e2e8f0",
                  },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </motion.div>
          </Box>
        </motion.div>

        {/* Pagination Info */}
        <motion.div variants={itemVariants}>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: { xs: 10, sm: 0 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                backgroundColor: "#f8fafc",
                borderRadius: 2,
                px: 3,
                py: 1,
                border: "1px solid #e2e8f0",
                fontWeight: 500,
              }}
            >
              {pagination.total > 0
                ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, pagination.total)} of ${pagination.total} users (Page ${currentPage} of ${pagination.totalPages})`
                : `No users to display${searchQuery ? " for search: " + searchQuery : ""}`}
            </Typography>
          </Box>
        </motion.div>
      </motion.div>

      {/* Import Users Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Users</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload a <b>.xlsx</b> file to import users.
          </Typography>
          <Button
            variant="contained"
            component="label"
            sx={{
              mb: 2,
              background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
              color: "white",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #16B1C8 0%, #20d5d5 100%)",
                boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
              },
            }}
            startIcon={<CloudUploadIcon />}
            disabled={importing}
          >
            Select File (.xlsx)
            <input type="file" accept=".xlsx" hidden onChange={handleFileChange} disabled={importing} />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <b>Selected:</b> {selectedFile.name}
            </Typography>
          )}
          {importing && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ ml: 1 }}>
                {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportUsers}
            disabled={!selectedFile || importing}
            startIcon={importing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            sx={{
              background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
              color: "white",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #16B1C8 0%, #20d5d5 100%)",
                boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
              },
            }}
          >
            {importing ? "Importing..." : "Import Users"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default UserOrdersPage
